/**
 * The `meta` key-value table introduced in database version 10.
 *
 * It holds what the installation knows about itself — nothing operational, and
 * nothing a screen renders. A migration that must reach devices already in the
 * field writes its revision here once it has run, which is what keeps it from
 * running a second time and undoing whatever the operator has done since.
 *
 * The table is deliberately absent from `BACKUP_TABLES`: a backup carries the
 * unit's data, not the migration history of the device it was taken on.
 */
import { db } from './index';
import type { MetaRecord } from '../types/meta';

const nowIso = (now: number = Date.now()): string => new Date(now).toISOString();

/** The stored value, or `null` when nothing has ever been written under it. */
export async function readMeta<T>(key: string): Promise<T | null> {
  const row = (await db.meta.get(key)) as MetaRecord<T> | undefined;
  return row === undefined ? null : row.value;
}

/** Writes — or overwrites — one fact, stamped with the moment it was filed. */
export async function writeMeta<T>(key: string, value: T): Promise<void> {
  await db.meta.put({ key, value, updatedAt: nowIso() });
}

/** Forgets one fact, so whatever wrote it will run again. */
export async function clearMeta(key: string): Promise<void> {
  await db.meta.delete(key);
}
